/**
 * useResellerStanding (TMMPDM-0023) — the reseller detail page's ONE read of
 * `GET /v1/admin/resellers/{id}/standing`. `AdminReseller.standing` is list-only (tmm-api 0077), so
 * the detail header, Overview, Tenants and Standing tabs all read this value instead. While it is
 * loading or failed `standing` is `null`: no chip, and nothing is disabled on its account.
 * `reload()` re-reads it (after a write, or the Standing tab's Retry).
 */
import { useCallback, useEffect, useState } from 'react';
import { adminApi } from '@/api/tmm/admin';
import type { ResellerStandingView } from '@/api/tmm/types';

export interface UseResellerStanding {
  /** The current response; `null` while loading or after a failure. */
  standing: ResellerStandingView | null;
  /** The last load's error, if it failed. */
  failure: unknown;
  loading: boolean;
  reload: () => void;
}

/** `resellerId` `null` = no valid id: nothing is fetched. `reloadKey` re-reads when it changes. */
export function useResellerStanding(
  resellerId: number | null,
  reloadKey: number,
): UseResellerStanding {
  const [retryToken, setRetryToken] = useState(0);
  const requestKey = `${resellerId}:${reloadKey}:${retryToken}`;
  const [response, setResponse] = useState<{
    requestKey: string | null;
    standing: ResellerStandingView | null;
    failure: unknown;
  }>({ requestKey: null, standing: null, failure: null });

  useEffect(() => {
    if (resellerId === null) return;
    let cancelled = false;
    adminApi
      .getResellerStanding(resellerId)
      .then((standing) => {
        if (!cancelled) setResponse({ requestKey, standing, failure: null });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setResponse({ requestKey, standing: null, failure });
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, requestKey]);

  const reload = useCallback(() => setRetryToken((n) => n + 1), []);
  const current = response.requestKey === requestKey;

  return {
    standing: current ? response.standing : null,
    failure: current ? response.failure : null,
    loading: resellerId !== null && !current,
    reload,
  };
}
